<template>
    <router-link class="campaign-card" :to="link" data-testid="campaign-card-link">
        <card is-clickable>
            <component
                :is="CampaignTypeIcon[campaign.campaignType]"
                v-if="campaign.campaignType"
                class="campaign-card__type-icon"
            />

            <div class="campaign-card__logo" :style="logoStyle">
                <Icon v-if="!campaign.mastheadImageUrl" type="ios-image-outline" class="campaign-card__missing-logo" />
            </div>
            <template #title>
                {{ campaign.name }}
            </template>
            <template #actionItems>
                <Dropdown transfer trigger="click">
                    <card-footer-button data-testid="campaign-card__more-button">
                        <Icon type="ios-more" class="campaign-card-option--dots" />
                    </card-footer-button>
                    <template #list>
                        <DropdownMenu>
                            <DropdownItem
                                v-if="userCanEditCampaign"
                                data-testid="campaign-card__edit-button"
                                @click="edit"
                            >
                                Edit
                            </DropdownItem>
                            <DropdownItem data-testid="campaign-card__manage-team-button" @click="manageTeam">
                                Manage Team
                            </DropdownItem>
                            <DropdownItem
                                v-if="userCanEditCampaign"
                                data-testid="campaign-card__move-section-button"
                                @click="setSection"
                            >
                                Move to Section
                            </DropdownItem>
                            <DropdownItem
                                v-if="userCanAddCampaign"
                                data-testid="campaign-card__duplicate-button"
                                @click="duplicateCampaign"
                            >
                                Duplicate
                            </DropdownItem>
                        </DropdownMenu>
                    </template>
                </Dropdown>
            </template>
        </card>
    </router-link>
</template>

<script lang="ts">
import { defineComponent, markRaw, type PropType } from "vue";

import { USER_ACTIONS } from "hoxton-shared/enums/user";

import Card from "@/components/Card/Card.vue";
import CardFooterButton from "@/components/Card/CardFooterButton.vue";
import { AppRouteNames } from "@/router/constants";

import HTML5Icon from "@/assets/html5_icon.svg";
import PhotoshopIcon from "@/assets/photoshop_icon.svg";
import AEIcon from "@/assets/ae_icon.svg";
import InDesignIcon from "@/assets/Adobe_Indesign_CC_2026_icon.svg";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import type { Campaign } from "@/types/types";
import { userCan } from "@/services/Auth";

const CampaignTypeIcon = markRaw({
    [TemplateType.AE]: AEIcon,
    [TemplateType.HTML5]: HTML5Icon,
    [TemplateType.PSD]: PhotoshopIcon,
    [TemplateType.INDESIGN]: InDesignIcon
});

export default defineComponent({
    components: {
        Card,
        CardFooterButton
    },
    props: {
        campaign: {
            required: true,
            type: Object as PropType<Campaign>
        }
    },

    emits: ["duplicate", "edit", "manage-team", "set-section"],

    data() {
        return {
            CampaignTypeIcon
        };
    },

    computed: {
        clientId() {
            return this.$route.params.clientId as string;
        },
        link() {
            const hasPermission = userCan(USER_ACTIONS.canManageOverwrites, {
                clientId: this.clientId,
                campaignId: this.campaign._id
            });

            const linkName = hasPermission ? AppRouteNames.CAMPAIGN_EDITOR : AppRouteNames.CAMPAIGN_LIBRARY;

            return {
                name: linkName,
                params: {
                    campaignId: this.campaign._id,
                    clientId: this.clientId
                },
                query: { j: (!!this.campaign.jiraTicketUrl).toString() }
            };
        },
        logoStyle() {
            if (this.campaign.mastheadImageUrl) {
                return {
                    backgroundImage: `url(${this.campaign.mastheadImageUrl})`
                };
            }
            return {};
        },
        userCanAddCampaign() {
            return userCan(USER_ACTIONS.canAddCampaign, {
                clientId: this.clientId
            });
        },
        userCanEditCampaign() {
            return userCan(USER_ACTIONS.canEditCampaign, {
                clientId: this.clientId,
                campaignId: this.campaign._id
            });
        }
    },
    methods: {
        duplicateCampaign() {
            this.$emit("duplicate");
        },
        edit() {
            this.$emit("edit");
        },
        manageTeam() {
            this.$emit("manage-team");
        },
        setSection() {
            this.$emit("set-section");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

$logo-height: 200px;

.campaign-card__logo {
    align-items: center;
    background: no-repeat center;
    background-size: contain;
    color: $grey5;
    display: flex;
    height: $logo-height;
    justify-content: center;
    width: 100%;
}

.campaign-card__logo--ci-demo {
    background-size: contain;
    max-height: 75%;
    max-width: 75%;
    flex: 0 0 auto;
    margin: 0 auto;
}

.campaign-card__missing-logo {
    align-items: center;
    display: flex;
    height: $logo-height;
    font-size: 80px;
    justify-content: center;
}

.campaign-card__type-icon {
    position: absolute;
    top: 10px;
    right: 15px;
    width: 25px;
    height: 25px;
}
</style>
